import React, { useState, useRef, useEffect } from 'react';
import { Users, UserCheck, UserMinus, X, ChevronRight } from 'lucide-react';
import './OverviewPanel.css';

const ON_DUTY_EMPLOYEES = [
  { id: 'PC001', name: 'Sai Kiran', unit: 'Police Constable', phone: '+91 98765 43001', badge: '#3b82f6', location: 'MG Road', since: '06:00 AM' },
];

const OFFLINE_EMPLOYEES = [
  { id: 'SS001', name: 'Sravani',  unit: 'Shakthi Team',     phone: '+91 98765 43001', badge: '#ec4899', reason: 'Day Off', lastSeen: '1h ago' },
  { id: 'TC001', name: 'Ravi Teja',    unit: 'Traffic Constable', phone: '+91 98765 43001', badge: '#dbc052', reason: 'Day Off', lastSeen: '2h ago' },
];

// ── Employee Detail Popup ──────────────────────────────────────────────────
const EmployeePopup = ({ type, onClose }) => {
  const employees  = type === 'duty' ? ON_DUTY_EMPLOYEES : OFFLINE_EMPLOYEES;
  const title      = type === 'duty' ? 'On Duty Officers' : 'Inactive / Offline Officers';
  const accentColor= type === 'duty' ? '#22c55e' : '#a855f7';

  // Group by unit
  const grouped = employees.reduce((acc, emp) => {
    acc[emp.unit] = acc[emp.unit] || [];
    acc[emp.unit].push(emp);
    return acc;
  }, {});

  return (
    <div className="emp-popup-overlay" onClick={onClose}>
      <div className="emp-popup" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="emp-popup-header" style={{ borderColor: accentColor }}>
          <span className="emp-popup-title" style={{ color: accentColor }}>{title}</span>
          <span className="emp-popup-count" style={{ color: accentColor }}>{employees.length}</span>
          <button className="emp-popup-close" onClick={onClose}><X size={14} /></button>
        </div>

        {/* Body grouped by unit */}
        <div className="emp-popup-body">
          {Object.entries(grouped).map(([unit, list]) => (
            <div key={unit} className="emp-unit-group">
              <div className="emp-unit-heading">
                <span className="emp-unit-dot" style={{ background: list[0].badge }} />
                {unit}
                <span className="emp-unit-chip">{list.length}</span>
              </div>

              {list.map(emp => (
                <div key={emp.id} className="emp-row">
                  <div className="emp-avatar" style={{ borderColor: emp.badge }}>
                    {emp.name.split(' ').map(n => n[0]).join('').slice(0, 2)}
                  </div>
                  <div className="emp-meta">
                    <div className="emp-name">{emp.name}</div>
                    <div className="emp-id">{emp.id}</div>
                    <div className="emp-phone">📞 {emp.phone}</div>
                  </div>
                  <div className="emp-detail">
                    {type === 'duty' ? (
                      <>
                        <div className="emp-tag green">{emp.location}</div>
                        <div className="emp-since">Since {emp.since}</div>
                      </>
                    ) : (
                      <>
                        <div className="emp-tag purple">{emp.reason}</div>
                        <div className="emp-since">Last seen {emp.lastSeen}</div>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ── Clickable Stat Card ────────────────────────────────────────────────────
const StatCard = ({ icon: Icon, color, label, value, sub, onClick, clickable }) => (
  <div
    className={`stat-card fade-in${clickable ? ' stat-card--clickable' : ''}`}
    onClick={onClick}
    title={clickable ? `Click to view details` : undefined}
  >
    <div className="stat-icon" style={{ color }}>
      <Icon size={28} />
    </div>
    <div className="stat-info">
      <div className="stat-label">{label}</div>
      <div className="stat-value" style={{ color }}>{value?.toLocaleString()}</div>
      {sub && <div className="stat-sub">{sub}</div>}
    </div>
    {clickable && (
      <div className="stat-card-cta" style={{ color }}>
        <ChevronRight size={16} />
      </div>
    )}
  </div>
);

// ── Unit Breakdown Row ─────────────────────────────────────────────────────
const UnitBreakdownRow = ({ dotColor, label, onDuty, offline }) => (
  <div className="unit-breakdown-row">
    <span className="unit-dot" style={{ background: dotColor }} />
    <span className="unit-breakdown-label">{label}</span>
    <span className="unit-breakdown-duty" style={{ color: dotColor }}>{onDuty}</span>
    <span className="unit-breakdown-sep">/</span>
    <span className="unit-breakdown-off">{offline}</span>
  </div>
);

// ── Main Panel ─────────────────────────────────────────────────────────────
const OverviewPanel = ({ data, alertCounts }) => {
  const [popup, setPopup] = useState(null); // 'duty' | 'offline' | null

  const totalBatons   = data?.totalBatons ?? 3;
  const onDuty        = data?.onDuty      ?? 1;
  const offline       = totalBatons - onDuty;
  const activePercent = Math.round((onDuty / totalBatons) * 100);

  return (
    <>
      <div className="overview-panel">
        <div className="panel-section">
          <div className="section-title">OVERVIEW</div>

          {/* Total — not clickable */}
          <StatCard
            icon={Users}
            color="#3b82f6"
            label="Total Batons Deployed"
            value={totalBatons}
            sub="Vijayawada City Police"
          />

          {/* On Duty — clickable */}
          <StatCard
            icon={UserCheck}
            color="#22c55e"
            label="On Duty"
            value={onDuty}
            sub={`${activePercent}% of deployed`}
            clickable
            onClick={() => setPopup('duty')}
          />

          {/* Offline — clickable */}
          <StatCard
            icon={UserMinus}
            color="#a855f7"
            label="Inactive / Offline"
            value={offline}
            sub={`${100 - activePercent}% of deployed`}
            clickable
            onClick={() => setPopup('offline')}
          />
        </div>

        {/* Unit Breakdown */}
        <div className="panel-section">
          <div className="section-title">UNIT BREAKDOWN</div>

          <div className="unit-breakdown-header">
            <span className="unit-breakdown-h-label">Unit</span>
            <span className="unit-breakdown-h-duty">Duty</span>
            <span className="unit-breakdown-h-sep" />
            <span className="unit-breakdown-h-off">Off</span>
          </div>

          <UnitBreakdownRow dotColor="#3b82f6" label="Police Constable" onDuty={1} offline={0} />
          <UnitBreakdownRow dotColor="#ec4899" label="Shakthi Team"   onDuty={0} offline={1} />
          <UnitBreakdownRow dotColor="#dbc052" label="Traffic Constable" onDuty={0} offline={1} />

          <div className="unit-bar-stack">
            <div className="unit-bar-fill-pc" style={{ width: `100%`, background: '#3b82f6' }} title="Police Constable: 1" />
            <div className="unit-bar-fill-ss" style={{ width: `0%`, background: '#ec4899' }} title="Shakthi Team: 0" />
            <div className="unit-bar-fill-tc" style={{ width: `0%`, background: '#dbc052' }} title="Traffic Constable: 0" />
          </div>
          <div className="unit-bar-legend">
            <span style={{ color: '#3b82f6' }}>● Police Constable</span>
            <span style={{ color: '#ec4899' }}>● Shakthi Team</span>
            <span style={{ color: '#dbc052' }}>● Traffic</span>
          </div>
        </div>
      </div>

      {/* Employee Detail Popup */}
      {popup && <EmployeePopup type={popup} onClose={() => setPopup(null)} />}
    </>
  );
};

export default OverviewPanel;
